/**
 * TextEditPanel — the drawer a superadmin edits interface text in.
 *
 * Both languages are always on screen together, because the site never ships
 * a label in one language only. Saving writes both in a single request, and
 * the page behind the drawer shows the new wording straight away.
 */

import { useEffect, useState } from 'react';
import { useT, resolveText } from '@/lib/i18n';
import { T } from '@/components/T';
import { useLanguageStore } from '@/stores/languageStore';
import { useUiTextStore, getPageKeys } from '@/stores/uiTextStore';
import { useLandingContentStore, isLandingKey, landingValue } from '@/stores/landingContentStore';
import RichTextField from './RichTextField';
import enLocale from '@/locales/en';
import deLocale from '@/locales/de';

interface TextEditPanelProps {
  /** The key being edited, or null while browsing everything on the page. */
  textKey: string | null;
  onClose: () => void;
}

export default function TextEditPanel({ textKey, onClose }: TextEditPanelProps) {
  const t = useT();
  const language = useLanguageStore((s) => s.language);
  const overrides = useUiTextStore((s) => s.overrides);
  const saving = useUiTextStore((s) => s.saving);
  const storeError = useUiTextStore((s) => s.error);
  const save = useUiTextStore((s) => s.save);
  const reset = useUiTextStore((s) => s.reset);
  const openEditor = useUiTextStore((s) => s.openEditor);

  const landingContent = useLandingContentStore((s) => s.content);
  const saveLanding = useLandingContentStore((s) => s.save);

  const [en, setEn] = useState('');
  const [de, setDe] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [showList, setShowList] = useState(!textKey);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);

  // Homepage copy lives in the landing content API rather than the locale
  // files; everything the superadmin sees and does is the same either way.
  const isLanding = isLandingKey(textKey ?? '');
  const hasOverride = Boolean(textKey && !isLanding && (overrides.en[textKey] || overrides.de[textKey]));

  /** What a key currently reads as, whichever store it comes from. */
  const textFor = (key: string): string =>
    isLandingKey(key) ? landingValue(landingContent, key, language) : resolveText(key, language, overrides).value;

  // Load the key being edited: the stored text if there is one, otherwise the
  // wording the site ships with, so the superadmin always starts from what is
  // on screen rather than an empty box.
  useEffect(() => {
    if (!textKey) return;
    if (isLandingKey(textKey)) {
      setEn(landingContent[textKey] ?? '');
      setDe(landingContent[`${textKey}_de`] ?? '');
    } else {
      setEn(overrides.en[textKey] ?? enLocale[textKey] ?? '');
      setDe(overrides.de[textKey] ?? deLocale[textKey] ?? '');
    }
    setLocalError(null);
    setShowList(false);
  }, [textKey, overrides, landingContent]);

  const onSave = async () => {
    if (!textKey) return;
    if (!en.trim() || !de.trim()) {
      setLocalError(t('textEditor.bothRequired'));
      return;
    }
    setLocalError(null);
    setBusy(true);
    try {
      if (isLanding) {
        await saveLanding(textKey, en, de);
        onClose();
      } else {
        await save(textKey, en, de);
      }
    } catch (e: unknown) {
      setLocalError((e as Error)?.message || t('textEditor.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const onReset = async () => {
    if (!textKey) return;
    if (!window.confirm(t('textEditor.resetConfirm'))) return;
    try {
      await reset(textKey);
    } catch {
      /* the store keeps the message */
    }
  };

  const pageKeys = showList ? getPageKeys() : [];
  const filtered = filter.trim()
    ? pageKeys.filter((k) => {
        const needle = filter.toLowerCase();
        if (k.toLowerCase().includes(needle)) return true;
        return textFor(k).toLowerCase().includes(needle);
      })
    : pageKeys;

  return (
    <aside className="artext-panel" role="dialog" aria-label={t('textEditor.title')}>
      <header className="artext-panel-head">
        <div>
          <h2 className="artext-panel-title">{textKey ? t('textEditor.title') : t('textEditor.browseTitle')}</h2>
          {textKey && <code className="artext-panel-key">{textKey}</code>}
        </div>
        <button type="button" className="artext-icon-btn" onClick={onClose} aria-label={t('textEditor.close')}>
          <i className="fas fa-xmark" />
        </button>
      </header>

      <p className="artext-hint">{textKey ? t('textEditor.hint') : t('textEditor.browseHint')}</p>

      {textKey && (
        <>
          <RichTextField label={t('textEditor.english')} value={en} onChange={setEn} placeholder={t('textEditor.englishPlaceholder')} />
          <RichTextField label={t('textEditor.german')} value={de} onChange={setDe} placeholder={t('textEditor.germanPlaceholder')} />

          {(localError || storeError) && (
            <p className="artext-error" role="alert">{localError || storeError}</p>
          )}

          <div className="artext-actions">
            <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving || busy}>
              <i className={`fas ${saving || busy ? 'fa-spinner fa-spin' : 'fa-floppy-disk'}`} /> <T k="textEditor.save" />
            </button>
            <button type="button" className="btn" onClick={onClose} disabled={saving || busy}>
              <T k="textEditor.cancel" />
            </button>
            {hasOverride && (
              <button type="button" className="btn artext-reset-btn" onClick={onReset} disabled={saving || busy}>
                <i className="fas fa-rotate-left" /> <T k="textEditor.reset" />
              </button>
            )}
          </div>
        </>
      )}

      <section className="artext-list-section">
        <button type="button" className="artext-list-toggle" onClick={() => setShowList((v) => !v)}>
          <i className={`fas ${showList ? 'fa-chevron-down' : 'fa-chevron-right'}`} /> <T k="textEditor.pageList" />
        </button>

        {showList && (
          <>
            <p className="artext-hint"><T k="textEditor.pageListHint" /></p>
            <input
              className="artext-search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t('textEditor.searchPlaceholder')}
            />
            <ul className="artext-key-list">
              {filtered.map((k) => (
                <li key={k}>
                  <button type="button" className={k === textKey ? 'artext-key-active' : undefined} onClick={() => openEditor(k)}>
                    <span className="artext-key-text">{textFor(k)}</span>
                    <code>{k}</code>
                  </button>
                </li>
              ))}
              {filtered.length === 0 && <li className="artext-empty"><T k="textEditor.noMatches" /></li>}
            </ul>
          </>
        )}
      </section>
    </aside>
  );
}
